import { CONFIG } from "~/config";
import { icon, node } from "~/util/dom";
import ReportFormField from "./index";
import ReportFormImage from "./image";
import ReportFormFileloader from "./fileloader";
import { errorToMsg } from "~/report/util";
import { getMediaIds, getPhotoId, getPhotoMediaId } from "~/report/provider";
import { DOCUMENT_TYPE, ERROR, MAX_PHOTOS } from "~/report/const";
import { ISubmitData, ICommonOpts } from "~/report/interfaces";
import { _ } from "~/i18n";
import { store } from "~/state/store";

interface IOpts extends ICommonOpts {
	maxPhotos?: number;
	newType?: boolean;
	container: HTMLElement;
	onChange: (state: boolean, second: any) => void;
	onUploadStart?: () => void;
	getPhotoData: (photo: any, data: any) => any;
	submitError?: number;
	showInfoText?: boolean;
}

export default class ReportFormFieldPhotos extends ReportFormField {
	#opts: IOpts;

	#photosNode: HTMLDivElement;

	#fileloader: ReportFormFileloader;

	#errorNode: HTMLDivElement;

	#photos: Array<any> = [];

	#deletedPhotos: Array<File> = [];

	#errors = [];

	#isFull;

	constructor(opts: IOpts) {
		super(opts);
		this.#opts = {
			newType: false,
			maxPhotos: MAX_PHOTOS,
			onChange: () => { /**/ },
			onUploadStart: () => { /**/ },
			container: null,
			getPhotoData: () => { /**/ },
			submitError: 0,
			showInfoText: true,
			...opts,
		};
		this.#isFull = false;
		this.#build();
	}

	#build() {
		this.#photosNode = node("div", { className: "photos" });

		this.#fileloader = new ReportFormFileloader({
			parent: this.#photosNode,
			icon: icon("image-round"),
			textShort: _("add"),
			textLong: _("report.addPlacePhoto"),
			isExpanded: true,
			onChange: files => {
				this.#clearErrors();

				const { user } = store.getState().user;

				if (user.state === "green") {
					this.#addPhotos(files);
				} else {
					Mapy.getComponent("login").open({
						analyticsData: {
							loginOrigin: "errorReportForm",
						},
						motivation: "report",
					}).then(() => {
						this.#addPhotos(files);
					}, () => {});
				}
			},
			setMultiple: true,
			accept: "image/png, image/jpeg",
		});

		this.#errorNode = node("div", { className: "message" });
		const lang = CONFIG.defaultLang;
		const termsLink = `https://licence.mapy.${CONFIG.TLD}/?doc=mapy_lu&lang=${lang}`;
		const termsNode = node("p", {
			className: "terms",
			innerHTML: _("report.licenceAggr", {
				linkStart: `<a href="${termsLink}" target="_blank" rel="noopener">`,
				linkEnd: "</a>",
			}),
		});

		if (this.#opts.showInfoText) {
			const infoText = node("p", { className: "pre-text", innerHTML: _("report.photos.infoText") });

			super.container.append(infoText, this.#photosNode, termsNode, this.#errorNode);
		} else {
			super.container.append(this.#photosNode, termsNode, this.#errorNode);
		}
	}

	#removePhoto(photo, file: File) {
		const photoIndex = this.#photos.indexOf(photo);

		this.#deletedPhotos.push(file);

		if (photoIndex > -1) {
			this.#clearErrors();
			this.#photos.splice(photoIndex, 1);

			if (this.#isFull) {
				this.#isFull = false;
				this.#fileloader.show();
			}
			if (!this.#photos.length) {
				this.#fileloader.expand();
				super.container.classList.remove("with-terms");
				this.#opts.onChange(false, null);
			}
		}
	}

	#checkLength(length) {
		const checkedLenght = typeof length === "number" ? length : this.#photos.length;

		if (!checkedLenght && !this.#fileloader.isExpanded) {
			this.#fileloader.expand();
		}
		if (this.#isFull && checkedLenght < 20) {
			this.#isFull = false;
			this.#fileloader.show();
		} else if (!this.#isFull && checkedLenght >= 20) {
			this.#isFull = true;
			this.#fileloader.hide();
		}
	}

	#addPhotos(files: Array<File>) {
		const { maxPhotos } = this.#opts;

		try {
			if (files.length) {
				if (this.#fileloader.isExpanded) {
					this.#fileloader.condense();
				}

				const uploadPhotos = (length: number) => {
					const promises = [];

					while (files.length && promises.length + length < maxPhotos) {
						const file = files.shift();
						const image = new ReportFormImage({
							file,
							parent: this.#photosNode,
							onDomChange: () => this.#opts.container.dispatchEvent(new Event("resize-content")),
							onRemove: photo => {
								this.#removePhoto(photo, file);
							},
							upload: async classThis => {
								try {
									if (this.#opts.newType) {
										classThis.uploadProgress(0);

										const photoData = await getPhotoMediaId({
											file,
											onUpdate: percent => {
												classThis.uploadProgress(percent);
											},
										});

										if (photoData.mediaId > 0) {
											const deleted = this.#deletedPhotos.includes(file);

											if (!deleted) {
												/* eslint-disable-next-line */
												classThis.id = photoData.mediaId;
											}

											return "OK";
										}
									} else {
										const photoData = await getPhotoId(file, percent => {
											classThis.uploadProgress(percent);
										});

										classThis.id = photoData.photoId;

										return "OK";
									}
								} catch (exc) {
									/* eslint-disable no-console */
									console.log(exc);
								}

								return ERROR.UPLOAD;
							},
						});

						promises.push(image.settingFile);
						this.#checkLength(promises.length + length);
						image.settingFile
							.then(() => {
								const deleted = this.#deletedPhotos.includes(file);

								if (!deleted) {
									this.#photos.push(image);
								}
							})
							.catch(error => {
								this.#displayError(error);
							});
					}

					if (promises.length) {
						this.#opts.onUploadStart();
					}

					super.container.classList.add("with-terms");

					Promise.allSettled(promises)
						.then(() => {
							this.#checkLength(null);

							if (this.#photos.length < maxPhotos && files.length) {
								uploadPhotos(this.#photos.length);
							} else if (files.length) {
								this.#displayError(ERROR.MAX_PHOTOS);
							}

							this.#opts.onChange(!!this.#photos.length, null);
							super.container.classList.add("with-terms");
						});
				};

				uploadPhotos(this.#photos.length);
			} else {
				this.#checkLength(null);
				this.#displayError(ERROR.ERROR);
				this.#opts.onChange(!!this.#photos.length, null);
			}
		} catch (error) {
			this.#displayError(ERROR.ERROR);
			this.#opts.onChange(!!this.#photos.length, null);
		}
	}

	#clearErrors() {
		if (super.container.classList.contains("invalid")) {
			super.container.classList.remove("invalid");
		}
		if (this.#errors.length) {
			this.#errors = [];
			this.#errorNode.replaceChildren();
			this.#opts.container.dispatchEvent(new Event("resize-content"));
		}
	}

	#displayError(error: number) {
		if (!super.container.classList.contains("invalid")) {
			super.container.classList.add("invalid");
		}
		if (!this.#errors.includes(error)) {
			this.#errors.push(error);
			const message = errorToMsg(error);
			const messageNode = node("p", { innerText: message });

			this.#errorNode.append(messageNode);
			this.#opts.container.dispatchEvent(new Event("resize-content"));
		}
	}

	// při odeslání formuláře se provede upload fotografií,
	// pro každou úspěšně nahranou fotografii se vrátí její mediaId
	async onSubmit(data: any): Promise<ISubmitData> {
		try {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			const { approved } = await Mapy.ProviderLicence.check().promise;

			const license = approved ? approved.find(doc => doc.document_type === DOCUMENT_TYPE) : "";
			const photos = [];

			for (const photo of this.#photos) {
				const photoData = this.#opts.getPhotoData(photo, data);

				if (!photoData) {
					return {
						errorMsg: _("firmPhotos.error.photoUpload"),
						type: "photos",
					};
				}

				photos.push({ ...photoData, license });
			}

			const mediaIds = [];
			let failed = false;

			if (photos.length > 0) {
				// v nove verzi je id uz to, co potrebujeme
				if (this.#opts.newType) {
					for (const photo of photos) {
						mediaIds.push(photo.id);
					}
				} else {
					const response = await getMediaIds(photos);

					if (response.status >= 200 && response.status < 300) {
						// json.photos obsahuje mediaId každé úspěšně uložené fotografie
						const json = await response.json();

						if (json && json.photos instanceof Array) {
							for (const photo of json.photos) {
								if (photo.status === 200 && photo.mediaId) {
									mediaIds.push(photo.mediaId);
								} else {
									failed = true;
								}
							}
						}
					} else {
						failed = true;
					}
				}
			}

			// do formuláře se zapíší mediaIds úspěšně uložených fotografií
			return {
				errorMsg: failed ? _("firmPhotos.error.photoUpload") : "",
				type: "photos",
				mediaIds,
			};
		} catch (error) {
			return {
				errorMsg: errorToMsg(this.#opts.submitError),
				type: "photos",
			};
		}
	}
}
